import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  AlertColor,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import RequiredLabel from "../../atoms/RequiredLabel";
import {
  emailErrorMessage,
  emailRegex,
  phoneNumberErrorMessage,
  phoneNumberRegex,
} from "../../../utils/ValidationHelper";

interface NewOperatorModalProps {
  open: boolean;
  handleClose: () => void;
  handleNewOperator: () => void;
  handleNewOperatorChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
}

const NewOperatorModal: React.FC<NewOperatorModalProps> = ({
  open,
  handleClose,
  handleNewOperator,
  handleNewOperatorChange,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [validationErrors, setValidationErrors] = useState({
    operatorName: "",
    departmentName: "",
    operatorPhoneNumber: "",
    operatorEmail: "",
    memo: "",
    operatorType: "",
  });

  const [operatorDetails, setOperatorDetails] = useState({
    operatorName: "",
    departmentName: "",
    operatorPhoneNumber: "",
    operatorEmail: "",
    memo: "",
    operatorType: "",
  });

  const handleValidation = () => {
    const errors = {
      operatorName: !operatorDetails.operatorName
        ? "氏名(部署名)は必須です"
        : operatorDetails.operatorName.length > CHARACTER_LIMIT
        ? `氏名(部署名)は${CHARACTER_LIMIT}文字以内で入力してください`
        : "",
      departmentName: !operatorDetails.departmentName
        ? "部署名(会社名)は必須です。"
        : operatorDetails.departmentName.length > CHARACTER_LIMIT
        ? `部署名(会社名)は${CHARACTER_LIMIT}文字以内で入力してください`
        : "",
      operatorPhoneNumber: !operatorDetails.operatorPhoneNumber
        ? "連絡先(電話)は必須です"
        : !phoneNumberRegex.test(operatorDetails.operatorPhoneNumber)
        ? phoneNumberErrorMessage
        : "",
      operatorEmail: !operatorDetails.operatorEmail
        ? "連絡先(メール)は必須です"
        : !emailRegex.test(operatorDetails.operatorEmail)
        ? emailErrorMessage
        : "",
      memo:
        operatorDetails.memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください`
          : "",
      operatorType: !operatorDetails.operatorType
        ? `対象サービスを選択してください`
        : "",
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOperatorDetails({
      ...operatorDetails,
      [event.target.name]: event.target.value,
    });
    handleNewOperatorChange(event);
    // handleValidation();  // 入力のたびにバリデーションを実行し、入力が正しいとリアルタイムでエラーメッセージを削除　動きがおかしいので一旦コメントアウト
  };

  const handleRegister = async () => {
    if (handleValidation()) {
      setIsLoading(true);
      try {
        await handleNewOperator();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const closeModal = () => {
    // エラーメッセージをリセット
    setValidationErrors({
      operatorName: "",
      departmentName: "",
      operatorPhoneNumber: "",
      operatorEmail: "",
      memo: "",
      operatorType: "",
    });

    // 入力内容をリセット
    setOperatorDetails({
      operatorName: "",
      departmentName: "",
      operatorPhoneNumber: "",
      operatorEmail: "",
      memo: "",
      operatorType: "",
    });

    handleClose(); // 親コンポーネントの関数も実行
  };

  return (
    <Dialog open={open} onClose={closeModal}>
      <DialogTitle>新規オペレーター登録</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="operatorId"
          label="ID（登録時に自動で生成されます）"
          type="text"
          name="operatorId"
          fullWidth
          disabled
        />
        <TextField
          margin="dense"
          id="password"
          label="パスワード（登録時に自動で生成されます）"
          type="text"
          name="password"
          fullWidth
          disabled
        />
        <TextField
          margin="dense"
          id="operatorName"
          label={
            <span>
              氏名(部署名)
              <RequiredLabel />
            </span>
          }
          type="text"
          name="operatorName"
          fullWidth
          onChange={handleChange}
          error={!!validationErrors.operatorName}
          helperText={validationErrors.operatorName}
        />
        <TextField
          margin="dense"
          id="departmentName"
          label={
            <span>
              部署名(会社名)
              <RequiredLabel />
            </span>
          }
          type="text"
          name="departmentName"
          fullWidth
          onChange={handleChange}
          error={!!validationErrors.departmentName}
          helperText={validationErrors.departmentName}
        />
        <TextField
          margin="dense"
          id="operatorPhoneNumber"
          label={
            <span>
              連絡先(電話)
              <RequiredLabel />
            </span>
          }
          type="text"
          name="operatorPhoneNumber"
          fullWidth
          onChange={handleChange}
          error={!!validationErrors.operatorPhoneNumber}
          helperText={validationErrors.operatorPhoneNumber}
        />
        <TextField
          margin="dense"
          id="operatorEmail"
          label={
            <span>
              連絡先(メール)
              <RequiredLabel />
            </span>
          }
          type="text"
          name="operatorEmail"
          fullWidth
          onChange={handleChange}
          error={!!validationErrors.operatorEmail}
          helperText={validationErrors.operatorEmail}
        />
        <TextField
          margin="dense"
          id="memo"
          label="メモ"
          type="text"
          name="memo"
          fullWidth
          onChange={handleChange}
          error={!!validationErrors.memo}
          helperText={validationErrors.memo}
        />
        <TextField
          margin="dense"
          id="operatorType"
          label={
            <span>
              対象サービス（あとから変更できません）
              <RequiredLabel />
            </span>
          }
          select
          name="operatorType"
          fullWidth
          onChange={handleChange}
          error={!!validationErrors.operatorType}
          helperText={validationErrors.operatorType}
        >
          <MenuItem value={"autolock"}>FTスマートキー</MenuItem>
          <MenuItem value={"social"}>ミライロドア</MenuItem>
        </TextField>
        <TextField
          margin="dense"
          id="registrationDate"
          label="登録日"
          type="date"
          name="registrationDate"
          fullWidth
          onChange={handleNewOperatorChange}
          InputLabelProps={{ shrink: true }}
          disabled
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} color="primary" disabled={isLoading}>
          キャンセル
        </Button>
        <Button onClick={handleRegister} color="primary" disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} /> : "登録"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewOperatorModal;
